const NEUTRAL_SCALE_POINT = 4.5;
const LIGHTNESS_ADJUSTMENT_FACTOR = 0.30;

export const adjustHexByPerceptualScale = (baseHex: string, scale: number): string => {
  function hexToRgb(hex: string): [number, number, number] {
    hex = hex.replace(/^#/, '');
    if (hex.length === 3) {
      hex = hex.split('').map((c) => c + c).join('');
    }
    const red = parseInt(hex.substring(0, 2), 16);
    const green = parseInt(hex.substring(2, 4), 16);
    const blue = parseInt(hex.substring(4, 6), 16);

    return [red, green, blue];
  }

  function rgbToHsl(red: number, green: number, blue: number): [number, number, number] {
    red /= 255;
    green /= 255;
    blue /= 255;
    const max = Math.max(red, green, blue);
    const min = Math.min(red, green, blue);
    let hue = 0;
    let saturation = 0;
    const lightness = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      saturation = lightness > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case red: hue = ((green - blue) / d + (green < blue ? 6 : 0)); break;
        case green: hue = ((blue - red) / d + 2); break;
        case blue: hue = ((red - green) / d + 4); break;
      }
      hue /= 6;
    }
    return [hue, saturation, lightness];
  }

  function hslToRgb(hue: number, saturation: number, lightness: number): [number, number, number] {
    let red: number;
    let green: number;
    let blue: number;

    if (saturation === 0) {
      red = green = blue = lightness;
    } else {
      const hue2rgb = (p: number, q: number, t: number): number => {
        if (t < 0) {
          t += 1;
        }
        if (t > 1) {
          t -= 1;
        }
        if (t < 1 / 6) {
          return p + (q - p) * 6 * t;
        }
        if (t < 1 / 2) {
          return q;
        }
        if (t < 2 / 3) {
          return p + (q - p) * (2 / 3 - t) * 6;
        }
        return p;
      };
      const upperBoundForGivenLightness = lightness < 0.5
        ? lightness * (1 + saturation)
        : (lightness + saturation - lightness * saturation);
      const lowerBoundForGivenLightness = 2 * lightness - upperBoundForGivenLightness;
      red = hue2rgb(lowerBoundForGivenLightness, upperBoundForGivenLightness, hue + 1 / 3);
      green = hue2rgb(lowerBoundForGivenLightness, upperBoundForGivenLightness, hue);
      blue = hue2rgb(lowerBoundForGivenLightness, upperBoundForGivenLightness, hue - 1 / 3);
    }
    return [red * 255, green * 255, blue * 255];
  }
  const [r, g, b] = hexToRgb(baseHex);
  const [h, s, l] = rgbToHsl(r, g, b);

  const factor = (NEUTRAL_SCALE_POINT - scale) / NEUTRAL_SCALE_POINT;
  const newL = Math.max(0, Math.min(1, l + factor * LIGHTNESS_ADJUSTMENT_FACTOR));
  const [nr, ng, nb] = hslToRgb(h, s, newL);

  return `rgba(${Math.round(nr)}, ${Math.round(ng)}, ${Math.round(nb)}, 1)`;
};
